import { Box, Stack, Tab, Tabs } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ManageTagsFormDialog } from "components/ManageTags/ManageTagsFormDialog";
import { WorkflowTagPickerAdapter } from "components/ManageTags/WorkflowTagPickerAdapter";
import { EntityUpdate } from "components/ManageTags/types";
import { TierIIYearPicker } from "components/TierIIYearPicker";
import { currentTierIIReportingYear } from "encamp-shared/src/constants/tierii";
import { gql } from "generated-graphql";
import { FacilityPilotProgramStatus } from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useLinearIssueStatuses } from "hooks/useLinearIssueStatuses";
import pluralize from "pluralize";
import React, { useCallback, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import { AddPilotFacilityModal } from "./AddPilotFacilityModal";
import { EditPilotProgramModal } from "./EditPilotProgramModal";
import { EditPilotProgramLocalModal } from "./EditPilotProgramLocalModal";
import { FacilityPilotProgramLocal } from "./FacilityPilotProgramLocal";
import { FacilityPilotProgramStates } from "./FacilityPilotProgramStates";
import {
  getStateTags,
  LocalJurisdictionRow,
  PilotFacilityRow,
  StateRow,
  StatusFilter,
  ViewType,
} from "./pilotProgramUtils";

const FACILITY_PILOT_PROGRAM_STATES_QUERY = gql(`
  query FacilityPilotProgramStates($reportingYear: Int!) {
    facilityPilotProgramStates(reportingYear: $reportingYear) {
      items {
        state
        stateName
        verifiedReportCount
        pilotFacilityCount
        totalReportingFacilities
        status
        isStateEnabled
        portalTech
        readyStatus
        notes
        documentationComplete
        qaComplete
        pilotFacilities {
          facilityId
          facilityName
          streetAddress1
          city
          facilityState
          tenantId
          tenantName
          isDeniedTenant
          reportId
          reportOrgStatus
          reportEncampStatus
          workflowStatus
          workflowId
          workflowTags {
            id
            name
          }
          configId
          configNotes
          configCreatedAt
          configCreatedBy
          currentActivityType
          currentActivityDisplayName
        }
      }
    }
  }
`);

const FACILITY_PILOT_PROGRAM_LOCALS_QUERY = gql(`
  query FacilityPilotProgramLocals($reportingYear: Int!) {
    facilityPilotProgramLocals(reportingYear: $reportingYear) {
      items {
        state
        localJurisdictionName
        reportingYear
        verifiedReportCount
        pilotFacilityCount
        totalReportingFacilities
        status
        readyStatus
        notes
        documentationComplete
        qaComplete
        jurisdictionType
        jurisdictionMatchKey
        pilotFacilities {
          facilityId
          facilityName
          streetAddress1
          city
          facilityState
          tenantId
          tenantName
          isDeniedTenant
          reportId
          reportOrgStatus
          reportEncampStatus
          workflowStatus
          workflowId
          workflowTags {
            id
            name
          }
          configId
          configNotes
          configCreatedAt
          configCreatedBy
          currentActivityType
          currentActivityDisplayName
          isCohort
        }
      }
    }
  }
`);

const REMOVE_FACILITY_FROM_PILOT_PROGRAM_MUTATION = gql(`
  mutation RemoveFacilityFromPilotProgram($configId: ID!) {
    removeFacilityFromPilotProgram(configId: $configId)
  }
`);

const UPDATE_WORKFLOW_TAGS_BULK_MUTATION = gql(`
  mutation UpdateWorkflowTagsBulk($workflowUpdates: [WorkflowTagUpdateInput!]!) {
    updateWorkflowTagsBulk(workflowUpdates: $workflowUpdates) {
      id
      tags {
        id
        name
      }
    }
  }
`);

export function FacilityPilotProgram() {
  const [reportingYear, setReportingYear] = useState<number>(
    currentTierIIReportingYear
  );
  const location = useLocation();
  const navigate = useNavigate();
  
  // Derive viewType from URL path
  const viewType: ViewType = location.pathname.endsWith("/local") ? "local" : "states";
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<
    StateRow | LocalJurisdictionRow | null
  >(null);
  const [editingRow, setEditingRow] = useState<
    StateRow | LocalJurisdictionRow | null
  >(null);
  const [tagsDialogOpen, setTagsDialogOpen] = useState(false);
  const [showTagsConfirmation, setShowTagsConfirmation] = useState(false);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(null);

  // Handle status filter clicks - toggle filter if same, set if different
  // For "enabled" filter, cycle through: null → "enabled" → "notEnabled" → null
  const handleStatusFilterClick = useCallback((filter: StatusFilter) => {
    if (filter === "enabled") {
      // Cycle through: null → "enabled" → "notEnabled" → null
      setStatusFilter((current) => {
        if (current === null) return "enabled";
        if (current === "enabled") return "notEnabled";
        if (current === "notEnabled") return null;
        return "enabled"; // If something else is set, go to enabled
      });
    } else {
      // For other filters, toggle on/off
      setStatusFilter((current) => (current === filter ? null : filter));
    }
  }, []);

  const alerts = useAlerts();

  useBreadcrumb([
    {
      label: "Workflow Config",
      to: "/staff/workflow-config",
    },
    {
      label: "Facility Pilot Program",
      to: "/staff/workflow-config/pilot-program/states",
    },
    {
      label: viewType === "states" ? "States" : "Local Jurisdictions",
    },
  ]);

  const {
    data: statesData,
    loading: statesLoading,
    refetch: refetchStates,
  } = useQuery(FACILITY_PILOT_PROGRAM_STATES_QUERY, {
    variables: { reportingYear },
    fetchPolicy: "cache-and-network",
    skip: viewType !== "states",
  });

  const {
    data: localsData,
    loading: localsLoading,
    refetch: refetchLocals,
  } = useQuery(FACILITY_PILOT_PROGRAM_LOCALS_QUERY, {
    variables: { reportingYear },
    fetchPolicy: "cache-and-network",
    skip: viewType !== "local",
  });

  const loading = viewType === "states" ? statesLoading : localsLoading;
  const refetch = viewType === "states" ? refetchStates : refetchLocals;

  const [removeMutation, { loading: removeLoading }] = useMutation(
    REMOVE_FACILITY_FROM_PILOT_PROGRAM_MUTATION,
    {
      onCompleted: () => {
        alerts.success("Facility removed from pilot program");
        refetch();
      },
      onError: (error) => {
        alerts.error(`Failed to remove facility: ${error.message}`);
      },
    }
  );

  const [updateWorkflowTags, { loading: updateTagsLoading }] = useMutation(
    UPDATE_WORKFLOW_TAGS_BULK_MUTATION,
    {
      refetchQueries: [
        FACILITY_PILOT_PROGRAM_STATES_QUERY,
        FACILITY_PILOT_PROGRAM_LOCALS_QUERY,
      ],
    }
  );

  const handleRemoveFacility = useCallback(
    (configId: string) => {
      if (
        window.confirm(
          "Are you sure you want to remove this facility from the pilot program?"
        )
      ) {
        removeMutation({ variables: { configId } });
      }
    },
    [removeMutation]
  );

  const handleAddFacility = useCallback(
    (row: StateRow | LocalJurisdictionRow) => {
      setSelectedRow(row);
      setAddModalOpen(true);
    },
    []
  );

  const handleEditProgram = useCallback(
    (row: StateRow | LocalJurisdictionRow) => {
      setEditingRow(row);
      setEditModalOpen(true);
    },
    []
  );

  const handleModalClose = useCallback(() => {
    setAddModalOpen(false);
    setSelectedRow(null);
  }, []);

  const handleEditModalClose = useCallback(() => {
    setEditModalOpen(false);
    setEditingRow(null);
  }, []);

  const handleFacilityAdded = useCallback(() => {
    refetch();
    handleModalClose();
  }, [refetch, handleModalClose]);

  // Handle opening tags dialog for a state or local jurisdiction
  const handleOpenTagsDialog = useCallback(
    (row: StateRow | LocalJurisdictionRow) => {
      setSelectedRow(row);
      setTagsDialogOpen(true);
    },
    []
  );

  // Handle closing tags dialog
  const handleCloseTagsDialog = useCallback(() => {
    setTagsDialogOpen(false);
    setShowTagsConfirmation(false);
    setSelectedRow(null);
  }, []);

  // Handle submitting tag updates
  const handleSubmitTags = useCallback(
    async (updates: EntityUpdate[]) => {
      try {
        const workflowUpdates = updates.map((update) => ({
          workflowId: update.id,
          tagNames: update.tags,
        }));

        if (workflowUpdates.length > 0) {
          await updateWorkflowTags({
            variables: {
              workflowUpdates,
            },
          });
        }

        const workflowCount = workflowUpdates.length;
        alerts.success(
          `Successfully updated tags for ${workflowCount} ${pluralize(
            "workflow",
            workflowCount
          )}`
        );

        handleCloseTagsDialog();
      } catch (err) {
        alerts.error("An error occurred while updating the workflow tags", err);
      }
    },
    [updateWorkflowTags, alerts, handleCloseTagsDialog]
  );

  // Transform selected state workflows to dialog format
  const tagsDialogRows = useMemo(() => {
    if (!selectedRow) return [];
    return selectedRow.pilotFacilities
      .filter(
        (facility): facility is PilotFacilityRow & { workflowId: string } =>
          facility.workflowId !== null
      )
      .map((facility) => ({
        id: facility.workflowId,
        tags: facility.workflowTags?.map((tag) => tag.name) || [],
      }));
  }, [selectedRow]);

  // Get all rows from data (unfiltered for stats calculation)
  // Cast to our row types which are compatible with the GraphQL types
  const allStateRows = useMemo(
    () => (statesData?.facilityPilotProgramStates.items ?? []) as StateRow[],
    [statesData]
  );

  const allLocalRows = useMemo(
    () =>
      (localsData?.facilityPilotProgramLocals.items ??
        []) as LocalJurisdictionRow[],
    [localsData]
  );

  const allRows = viewType === "states" ? allStateRows : allLocalRows;

  // Collect all existing pilot facility IDs for the modal (use allRows to include all states)
  const existingPilotFacilityIds = useMemo(() => {
    const ids = new Set<string>();
    allRows.forEach((row) => {
      row.pilotFacilities.forEach((pf) => {
        ids.add(pf.facilityId);
      });
    });
    return ids;
  }, [allRows]);

  // Collect all tags from all rows for Linear issue lookup (call API once for entire datagrid)
  const allTagsForLinear = useMemo(() => {
    const tagSet = new Set<string>();
    allRows.forEach((row) => {
      const stateTags = getStateTags(row);
      stateTags.forEach((tag) => tagSet.add(tag));
      // Also collect tags from individual facilities in sub-datagrid
      row.pilotFacilities.forEach((facility) => {
        facility.workflowTags?.forEach((tag) => {
          tagSet.add(tag.name);
        });
      });
    });
    return Array.from(tagSet);
  }, [allRows]);

  const { statusMap: linearStatusMap } =
    useLinearIssueStatuses(allTagsForLinear);

  return (
    <Box sx={{ pt: 1, px: 3 }}>
      <Stack spacing={2} sx={{ mb: 2 }}>
        {/* Tabs for States vs Local Jurisdictions */}
        <Tabs
          value={viewType}
          onChange={(_, newValue) => navigate(`/staff/workflow-config/pilot-program/${newValue}`)}
          sx={{ borderBottom: 1, borderColor: "divider" }}
        >
          <Tab label="States" value="states" />
          <Tab label="Local Jurisdictions" value="local" />
        </Tabs>

      </Stack>

      {viewType === "states" ? (
        <FacilityPilotProgramStates
          allStateRows={allStateRows}
          loading={loading}
          statusFilter={statusFilter}
          linearStatusMap={linearStatusMap}
          onStatusFilterClick={handleStatusFilterClick}
          onAddFacility={handleAddFacility}
          onEditProgram={handleEditProgram}
          onOpenTagsDialog={handleOpenTagsDialog}
          onRemoveFacility={handleRemoveFacility}
          removeLoading={removeLoading}
          yearPicker={
            <TierIIYearPicker
              value={reportingYear}
              onChange={(value) => {
                if (value !== null && value !== undefined) {
                  const year =
                    typeof value === "number" ? value : parseInt(String(value), 10);
                  if (!isNaN(year)) {
                    setReportingYear(year);
                  }
                }
              }}
            />
          }
        />
      ) : (
        <FacilityPilotProgramLocal
          allLocalRows={allLocalRows}
          loading={loading}
          statusFilter={statusFilter}
          linearStatusMap={linearStatusMap}
          onStatusFilterClick={handleStatusFilterClick}
          onAddFacility={handleAddFacility}
          onEditProgram={handleEditProgram}
          onOpenTagsDialog={handleOpenTagsDialog}
          onRemoveFacility={handleRemoveFacility}
          removeLoading={removeLoading}
          yearPicker={
            <TierIIYearPicker
              value={reportingYear}
              onChange={(value) => {
                if (value !== null && value !== undefined) {
                  const year =
                    typeof value === "number" ? value : parseInt(String(value), 10);
                  if (!isNaN(year)) {
                    setReportingYear(year);
                  }
                }
              }}
            />
          }
        />
      )}

      {selectedRow && (
        <AddPilotFacilityModal
          open={addModalOpen}
          onClose={handleModalClose}
          onFacilityAdded={handleFacilityAdded}
          state={selectedRow.state}
          reportingYear={reportingYear}
          existingPilotFacilityIds={existingPilotFacilityIds}
          localJurisdictionName={
            "localJurisdictionName" in selectedRow
              ? (selectedRow as LocalJurisdictionRow).localJurisdictionName
              : undefined
          }
          jurisdictionType={
            "localJurisdictionName" in selectedRow
              ? (selectedRow as LocalJurisdictionRow).jurisdictionType
              : undefined
          }
          jurisdictionMatchKey={
            "localJurisdictionName" in selectedRow
              ? (selectedRow as LocalJurisdictionRow).jurisdictionMatchKey
              : undefined
          }
        />
      )}

      {editingRow && "isStateEnabled" in editingRow ? (
        <EditPilotProgramModal
          open={editModalOpen}
          onClose={handleEditModalClose}
          onSave={handleFacilityAdded}
          state={editingRow.state}
          reportingYear={reportingYear}
        />
      ) : editingRow && "localJurisdictionName" in editingRow ? (
        <EditPilotProgramLocalModal
          open={editModalOpen}
          onClose={handleEditModalClose}
          onSave={handleFacilityAdded}
          state={editingRow.state}
          localJurisdictionName={(editingRow as LocalJurisdictionRow).localJurisdictionName}
          reportingYear={reportingYear}
          initialData={{
            status: (editingRow as LocalJurisdictionRow).readyStatus,
            notes: (editingRow as LocalJurisdictionRow).notes ?? null,
            documentationComplete: (editingRow as LocalJurisdictionRow).documentationComplete,
            qaComplete: (editingRow as LocalJurisdictionRow).qaComplete,
            jurisdictionType: (editingRow as LocalJurisdictionRow).jurisdictionType,
            jurisdictionMatchKey: (editingRow as LocalJurisdictionRow).jurisdictionMatchKey,
          }}
        />
      ) : null}

      <ManageTagsFormDialog
        selectedRows={tagsDialogRows}
        getEntityId={(row) => row.id}
        onClose={handleCloseTagsDialog}
        onCloseConfirmation={() => setShowTagsConfirmation(false)}
        confirm={() => setShowTagsConfirmation(true)}
        open={tagsDialogOpen}
        withConfirmation={true}
        showConfirmation={showTagsConfirmation}
        onSubmit={handleSubmitTags}
        loading={updateTagsLoading}
        entityName="Workflow"
        localStorageKey="workflow-recent-tags"
        commonTags={["compliance", "engineering", "linear"]}
        TagPickerComponent={WorkflowTagPickerAdapter}
      />
    </Box>
  );
}
